import { z } from "zod";
import {
	abnormalFlagSchema,
	criticalAlertFlagSchema,
	criticalAlertStatusSchema,
} from "./clinical.ts";

/**
 * Results Inbox — the doctor's ONE queue of everything awaiting review
 * (Epic In-Basket style). Three buckets, strictly clinic-scoped, oldest
 * first within each bucket:
 *
 *   critical     — open (pending/notified) critical-value alerts AND open
 *                  radiology critical findings. Lives at the top in red.
 *   abnormal_lab — unverified abnormal lab values, grouped per lab order.
 *                  Values already covered by an OPEN critical alert are
 *                  EXCLUDED here (they're in the critical bucket — showing
 *                  them twice would double the perceived workload).
 *   radiology    — finalized reports the ordering doctor hasn't opened yet
 *                  (reviewed_at IS NULL).
 */

// ===== critical bucket =====

/** One open critical item — lab value alert or radiology critical finding. */
export const inboxCriticalItemSchema = z.object({
	kind: z.enum(["lab", "radiology"]),
	/** critical_value_alerts.id or radiology_critical_findings.id */
	id: z.string().uuid(),
	patient_id: z.string().uuid(),
	patient_name: z.string().nullable(),
	/** lab_orders.id / radiology_orders.id (lab alerts may be orderless). */
	order_id: z.string().uuid().nullable(),
	/** lab: test name · radiology: study name */
	label: z.string(),
	/** lab: formatted value · radiology: matched keyword / excerpt */
	detail: z.string().nullable(),
	unit: z.string().nullable(),
	/** Only lab alerts carry a numeric flag direction. */
	flag: criticalAlertFlagSchema.nullable(),
	status: criticalAlertStatusSchema,
	created_at: z.string(),
});
export type InboxCriticalItem = z.infer<typeof inboxCriticalItemSchema>;

// ===== abnormal_lab bucket =====

export const inboxAbnormalValueSchema = z.object({
	id: z.string().uuid(),
	test_name: z.string(),
	value_numeric: z.number().nullable(),
	value_text: z.string().nullable(),
	unit: z.string().nullable(),
	flag: abnormalFlagSchema,
	measured_at: z.string(),
});
export type InboxAbnormalValue = z.infer<typeof inboxAbnormalValueSchema>;

/** Unverified abnormal values grouped per lab order (null = orderless). */
export const inboxAbnormalOrderSchema = z.object({
	order_id: z.string().uuid().nullable(),
	patient_id: z.string().uuid(),
	patient_name: z.string().nullable(),
	accession_number: z.string().nullable(),
	tests: z.array(inboxAbnormalValueSchema),
	/** Oldest measured_at in the group — drives bucket ordering. */
	oldest_at: z.string(),
});
export type InboxAbnormalOrder = z.infer<typeof inboxAbnormalOrderSchema>;

// ===== radiology bucket =====

export const inboxRadiologyReportSchema = z.object({
	order_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	patient_name: z.string().nullable(),
	study_name: z.string(),
	modality: z.string(),
	/** First ~200 chars of the impression (fallback: findings). */
	impression_snippet: z.string().nullable(),
	finalized_at: z.string(),
	/** Manual critical declaration or a logged critical finding. */
	critical: z.boolean(),
});
export type InboxRadiologyReport = z.infer<typeof inboxRadiologyReportSchema>;

// ===== unsigned_visits bucket =====

/**
 * Visits left unsigned from PREVIOUS days (>24h old) — Epic's "unsigned
 * encounters". Today's active charting is deliberately not nagged.
 */
export const inboxUnsignedVisitSchema = z.object({
	visit_id: z.string().uuid(),
	appointment_id: z.string().uuid().nullable(),
	patient_id: z.string().uuid(),
	patient_name: z.string().nullable(),
	chief_complaint: z.string().nullable(),
	occurred_at: z.string(),
});
export type InboxUnsignedVisit = z.infer<typeof inboxUnsignedVisitSchema>;

// ===== envelope =====

export const resultsInboxCountsSchema = z.object({
	critical: z.number().int(),
	abnormal_lab: z.number().int(),
	radiology: z.number().int(),
	unsigned_visits: z.number().int(),
	total: z.number().int(),
});
export type ResultsInboxCounts = z.infer<typeof resultsInboxCountsSchema>;

export const resultsInboxSchema = z.object({
	critical: z.array(inboxCriticalItemSchema),
	abnormal_lab: z.array(inboxAbnormalOrderSchema),
	radiology: z.array(inboxRadiologyReportSchema),
	unsigned_visits: z.array(inboxUnsignedVisitSchema),
	counts: resultsInboxCountsSchema,
});
export type ResultsInboxApi = z.infer<typeof resultsInboxSchema>;
